import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SalesQuotaWizard } from '../SalesQuotaWizard';
import { DayColumn } from '../types/wizard';
import { toast } from 'react-hot-toast';

interface WizardPageProps {
    onLogout: () => void;
}

export const WizardPage: React.FC<WizardPageProps> = () => {
    const navigate = useNavigate();
    const [shouldResetWizard] = useState(false);

    const handleScheduleGenerated = async (scheduleData: DayColumn[]) => {
        try {
            // Store schedule data with fallback strategies for large datasets
            await storeScheduleDataSafely(scheduleData);

            // Get task_id from sessionStorage (saved during optimization)
            const taskId = sessionStorage.getItem('dexco-task-id');

            // Navigate to unified schedule page in the same page
            if (taskId) {
                navigate(`/cronograma/${taskId}`);
            } else {
                // Fallback to old route if no task_id available
                navigate('/schedule');
            }

            console.log('✅ Navigating to schedule page with data stored safely');
        } catch (error) {
            console.error('❌ Error handling schedule generation:', error);
            toast.error('Erro ao abrir priorização');
        }
    };

    // Safe storage function with multiple fallback strategies
    const storeScheduleDataSafely = async (scheduleData: DayColumn[]): Promise<void> => {
        const dataString = JSON.stringify(scheduleData);
        const dataSizeKB = Math.round(dataString.length / 1024);
        
        console.log(`📊 Schedule data size: ${dataSizeKB} KB`);

        try {
            // Strategy 1: Try direct sessionStorage (works for smaller datasets)
            sessionStorage.setItem('dexco-schedule-data', dataString);
            console.log('✅ Stored in sessionStorage directly');
            return;
        } catch (quotaError) {
            console.warn(`⚠️ SessionStorage quota exceeded (${dataSizeKB} KB), trying fallbacks...`);
            
            try {
                // Strategy 2: Clear other sessionStorage items and retry
                const importantKeys = ['dexco-task-id', 'dexco-auth-token'];
                const preservedData: Record<string, string> = {};
                
                // Preserve important data
                importantKeys.forEach(key => {
                    const value = sessionStorage.getItem(key);
                    if (value) preservedData[key] = value;
                });
                
                // Clear sessionStorage
                sessionStorage.clear();
                
                // Restore important data
                Object.entries(preservedData).forEach(([key, value]) => {
                    sessionStorage.setItem(key, value);
                });
                
                // Try storing again
                sessionStorage.setItem('dexco-schedule-data', dataString);
                console.log('✅ Stored after clearing sessionStorage');
                return;
            } catch (secondError) {
                console.warn('⚠️ Still quota exceeded after clearing, trying compression...');
                
                try {
                    // Strategy 3: Store compressed/chunked data
                    await storeDataInChunks(scheduleData);
                    console.log('✅ Stored using chunking strategy');
                    return;
                } catch (chunkError) {
                    console.error('❌ All storage strategies failed:', chunkError);
                    
                    // Strategy 4: Store minimal data and use API for full data
                    const minimalData = {
                        totalDays: scheduleData.length,
                        totalDeliveries: scheduleData.reduce((sum, day) => sum + day.deliveries.length, 0),
                        taskId: sessionStorage.getItem('dexco-task-id'),
                        timestamp: Date.now()
                    };
                    
                    sessionStorage.setItem('dexco-schedule-minimal', JSON.stringify(minimalData));
                    console.log('✅ Stored minimal data as fallback');
                    
                    // Show warning to user
                    toast('Cronograma muito grande. Usando modo otimizado.', {
                        icon: '⚠️',
                        duration: 4000,
                    });
                }
            }
        }
    };

    // Function to store data in chunks
    const storeDataInChunks = async (scheduleData: DayColumn[]): Promise<void> => {
        const CHUNK_SIZE = 50; // Limit to 50 days per chunk
        const chunks: DayColumn[][] = [];
        
        // Clear any existing chunks first
        clearExistingChunks();
        
        // Split data into chunks
        for (let i = 0; i < scheduleData.length; i += CHUNK_SIZE) {
            chunks.push(scheduleData.slice(i, i + CHUNK_SIZE));
        }
        
        // Store chunk metadata
        sessionStorage.setItem('dexco-schedule-chunks', chunks.length.toString());
        
        // Store each chunk
        chunks.forEach((chunk, index) => {
            sessionStorage.setItem(`dexco-schedule-chunk-${index}`, JSON.stringify(chunk));
        });
        
        console.log(`📦 Stored ${chunks.length} chunks`);
    };

    // Helper function to clear existing chunks
    const clearExistingChunks = (): void => {
        const existingChunks = sessionStorage.getItem('dexco-schedule-chunks');
        if (existingChunks) {
            const chunkCount = parseInt(existingChunks);
            for (let i = 0; i < chunkCount; i++) {
                sessionStorage.removeItem(`dexco-schedule-chunk-${i}`);
            }
            sessionStorage.removeItem('dexco-schedule-chunks');
        }
    };

    const handleClose = () => {
        navigate('/home');
    };

    return (
        <SalesQuotaWizard
            onScheduleGenerated={handleScheduleGenerated}
            onClose={handleClose}
            shouldReset={shouldResetWizard}
        />
    );
};